import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Button,
    Typography,
    TextField,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    Box,
    Chip,
    Snackbar,
    Alert,
    Divider
} from '@mui/material';
import { ArrowBack, Save, Group as GroupIcon } from '@mui/icons-material';
import useCurrentUser from '../../../hooks/useCurrentUser';
import useAxios from '../../../hooks/useAxios';
import { ADMIN_VIEWS, USER_LIST_URL, UPDATE_GROUP_URL, GROUP_NAME_MAX_LENGTH, GROUP_DESCRIPTION_MAX_LENGTH } from '../../../utils/constant';
import { validateGroupForm } from '../../../utils/groupValidation';
import { User } from '../../../Models/User';
import { Group } from '../../../Models/Group';
import '../../../styles/NewGroupForm.css';
import { logger } from '../../../utils/logger';


interface EditGroupFormProps {
    selectedMenu: (view: number) => void;
    groupToEdit: Group;
    onUpdate: (updatedGroup: Group) => void;
}

const EditGroupForm: React.FC<EditGroupFormProps> = ({ selectedMenu, groupToEdit, onUpdate }) => {
    const { currentUser } = useCurrentUser();
    const { getWithAxios, putWithAxios } = useAxios();

    // États du formulaire
    const [groupName, setGroupName] = useState(groupToEdit.name);
    const [groupDescription, setGroupDescription] = useState(groupToEdit.description || '');
    const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
    const [availableUsers, setAvailableUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [isLoadingGroupMembers, setIsLoadingGroupMembers] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Recherche
    const [search, setSearch] = useState('');

    // Snackbar
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    // Erreur de formulaire
    const [formError, setFormError] = useState('');

    // Charger les membres du groupe
    useEffect(() => {
        const loadGroupMembers = async () => {
            if (!currentUser?.tokens?.accessToken) return;

            try {
                const response = await getWithAxios(`/admin/groups/${groupToEdit.uuid}/users`);
                const members = Array.isArray(response.users) ? response.users : [];
                const memberUuids = members.map((m: User) => m.uuid);
                setSelectedMembers(memberUuids);
            } catch (err) {
                logger.error('Erreur lors du chargement des membres du groupe:', err);
                // Si erreur, utiliser les memberUuids du groupe si disponibles
                setSelectedMembers(groupToEdit.memberUuids || []);
            } finally {
                setIsLoadingGroupMembers(false);
            }
        };

        loadGroupMembers();
    }, [currentUser, groupToEdit.uuid]);

    // Charger tous les utilisateurs
    useEffect(() => {
        const loadUsers = async () => {
            if (!currentUser?.tokens?.accessToken) return;

            try {
                const users = await getWithAxios(USER_LIST_URL);
                const allUsers = Array.isArray(users.data) ? users.data : [];
                setAvailableUsers(allUsers);
                setFilteredUsers(allUsers);
            } catch (err) {
                logger.error('Erreur lors du chargement des utilisateurs:', err);
                setSnackbarMessage('Erreur lors du chargement des utilisateurs');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            } finally {
                setIsLoadingUsers(false);
            }
        };
        loadUsers();
    }, [currentUser]);

    // Filtrage en fonction de la recherche
    useEffect(() => {
        if (!search.trim()) {
            setFilteredUsers(availableUsers);
        } else {
            const query = search.toLowerCase();
            setFilteredUsers(
                availableUsers.filter(
                    u =>
                        u.firstname.toLowerCase().includes(query) ||
                        u.lastname.toLowerCase().includes(query) ||
                        u.email.toLowerCase().includes(query)
                )
            );
        }
    }, [search, availableUsers]);

    // Sélection / désélection d’un membre
    const handleMemberToggle = (userUuid: string) => {
        setSelectedMembers(prev =>
            prev.includes(userUuid)
                ? prev.filter(id => id !== userUuid)
                : [...prev, userUuid]
        );
    };

    // Soumission de la mise à jour
    const handleSubmit = async () => {
        // Utiliser la validation centralisée
        const validationError = validateGroupForm({
            name: groupName,
            description: groupDescription,
            memberUuids: selectedMembers
        });

        if (validationError) {
            setFormError(validationError);
            return;
        }

        if (!currentUser?.tokens?.accessToken) return;

        setIsSubmitting(true);
        setFormError('');

        try {
            const payload = {
                name: groupName.trim(),
                description: groupDescription.trim() || undefined,
                memberUuids: selectedMembers
            };

            const updatedGroup = await putWithAxios(UPDATE_GROUP_URL(groupToEdit.uuid), payload);

            setSnackbarMessage('Groupe mis à jour avec succès !');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            onUpdate(updatedGroup);

            // attendre la fin du snackbar avant de changer de vue
            setTimeout(() => selectedMenu(ADMIN_VIEWS.GROUP_LIST_VIEW), 2000);
        } catch (err) {
            logger.error('Erreur lors de la mise à jour du groupe:', err);
            setSnackbarMessage('Erreur lors de la mise à jour du groupe');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        selectedMenu(ADMIN_VIEWS.GROUP_LIST_VIEW);
    };

    return (
        <div className="new-group-container">
            <div className="new-group-header">
                <div className="header-left">
                    <div className="section-icon">
                        <GroupIcon />
                    </div>
                    <Typography variant="h4" className="section-title">Modifier le Groupe</Typography>
                </div>
                <Button startIcon={<ArrowBack />} onClick={handleBack}>Retour</Button>
            </div>

            <div className="form-content">
                {/* Infos du groupe */}
                <Card className="group-info-card">
                    <CardContent>
                        <TextField
                            label="Nom du groupe"
                            value={groupName}
                            onChange={e => setGroupName(e.target.value)}
                            error={!!formError && !groupName.trim()}
                            helperText={formError && !groupName.trim() ? formError : `${groupName.length}/${GROUP_NAME_MAX_LENGTH} caractères`}
                            fullWidth
                            margin="normal"
                            required
                            inputProps={{ maxLength: GROUP_NAME_MAX_LENGTH }}
                        />
                        <TextField
                            label="Description"
                            value={groupDescription}
                            onChange={e => setGroupDescription(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                            margin="normal"
                            helperText={`${groupDescription.length}/${GROUP_DESCRIPTION_MAX_LENGTH} caractères`}
                            inputProps={{ maxLength: GROUP_DESCRIPTION_MAX_LENGTH }}
                        />
                    </CardContent>
                </Card>

                <Divider sx={{ my: 3 }} />

                {/* Sélection des membres */}
                <Card className="members-selection-card">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Sélectionner les membres
                        </Typography>

                        {/* Barre de recherche */}
                        <TextField
                            label="Rechercher un utilisateur"
                            variant="outlined"
                            size="small"
                            fullWidth
                            margin="dense"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        {/* Liste scrollable */}
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {isLoadingUsers ? (
                                <Typography>Chargement des utilisateurs...</Typography>
                            ) : (
                                <List dense>
                                    {filteredUsers.map(user => (
                                        <ListItem
                                            key={user.uuid}
                                            button
                                            onClick={() => handleMemberToggle(user.uuid)}
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={selectedMembers.includes(user.uuid)}
                                                />
                                            </ListItemIcon>
                                            <Avatar sx={{ mr: 2 }}>
                                                {user.firstname?.[0]}
                                                {user.lastname?.[0]}
                                            </Avatar>
                                            <ListItemText
                                                primary={`${user.firstname} ${user.lastname}`}
                                                secondary={user.email}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>

                        {/* Membres sélectionnés */}
                        {selectedMembers.length > 0 && (
                            <Box mt={2}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Membres ajoutés :
                                </Typography>
                                {selectedMembers.map(uuid => {
                                    const user = availableUsers.find(u => u.uuid === uuid);
                                    return user ? (
                                        <Chip
                                            key={uuid}
                                            avatar={<Avatar>{user.firstname?.[0]}</Avatar>}
                                            label={`${user.firstname} ${user.lastname}`}
                                            onDelete={() => handleMemberToggle(uuid)}
                                            sx={{ mr: 1, mb: 1 }}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ) : null;
                                })}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Actions */}
            <Box sx={{
                mt: 3,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                gap: 1
            }}>
                <Button
                    onClick={handleBack}
                    disabled={isSubmitting}
                    sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        order: { xs: 2, sm: 0 }
                    }}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSubmit}
                    disabled={isSubmitting || !groupName.trim()}
                    sx={{
                        background: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
                        },
                        '&.Mui-disabled': {
                            background: 'rgba(0, 0, 0, 0.12)'
                        }
                    }}
                >
                    {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default EditGroupForm;
